import {
  BirthdayVisibilityEnum,
  GenderEnum,
  PlayerFinderTimeOfDayEnum,
  SkillLevelValuesEnum,
  SportsEnum,
} from 'types/generated/client';
import type { AvailabilityDay } from 'utils/shared/playerAvailability';
import type { VenueAddressWithCity } from 'screens/ListGame/types';

/**
 * The player-finder form vocabulary, shared by the Find onboarding steps and the
 * in-place editors on the player profile. Kept beside playerAvailability rather
 * than in either screen so neither owns the other's types.
 */

/** The UI presents a subset of the generated `GenderEnum`. */
export const GENDER_OPTIONS = [
  { label: 'Male', value: GenderEnum.Male },
  { label: 'Female', value: GenderEnum.Female },
  { label: 'Nonbinary', value: GenderEnum.Nonbinary },
];

export const BIRTHDAY_VISIBILITY_OPTIONS = [
  { label: 'Visible', value: BirthdayVisibilityEnum.Visible },
  { label: 'Hide year', value: BirthdayVisibilityEnum.HideYear },
];

/** Each key maps to the matching `users.<key>_url` column. */
export enum SocialLink {
  Instagram = 'instagram',
  Facebook = 'facebook',
  Tiktok = 'tiktok',
  Linkedin = 'linkedin',
  X = 'x',
}

export const SOCIAL_LINK_OPTIONS = [
  { label: 'Instagram', value: SocialLink.Instagram, placeholder: 'instagram.com/yourhandle' },
  { label: 'Facebook', value: SocialLink.Facebook, placeholder: 'facebook.com/yourhandle' },
  { label: 'TikTok', value: SocialLink.Tiktok, placeholder: 'tiktok.com/@yourhandle' },
  { label: 'LinkedIn', value: SocialLink.Linkedin, placeholder: 'linkedin.com/in/yourhandle' },
  { label: 'X', value: SocialLink.X, placeholder: 'x.com/yourhandle' },
];

export interface ProfileBasicsValues {
  aboutMe: string;
  gender: GenderEnum | null;
  /** Native date input value (yyyy-MM-dd). */
  birthday: string;
  birthdayVisibility: BirthdayVisibilityEnum;
  /** Keyed by SocialLink; an empty string means the toggle is off. */
  socialLinks: Record<SocialLink, string>;
}

/**
 * An image already uploaded to the media provider (Cloudflare) via
 * `useUploadSignedFile` — the fields we persist to `users.profile_image_*` or a
 * `user_files` row. Photos upload on select, so forms hold metadata, not files.
 */
export interface UploadedImage {
  /** Set for a row already in user_files; absent for a fresh upload. */
  id?: string;
  fileName: string;
  originalFileName: string;
  path: string;
  provider: string;
  providerUrl: string;
  url: string;
  fileType: string;
  host?: string;
}

/** The upload response fields we keep for persistence. */
export const toUploadedImage = (payload: {
  fileName: string;
  originalFileName: string;
  path: string;
  provider: string;
  providerUrl: string;
  url: string;
  fileType: string;
  host?: string;
}): UploadedImage => ({
  fileName: payload.fileName,
  originalFileName: payload.originalFileName,
  path: payload.path,
  provider: payload.provider,
  providerUrl: payload.providerUrl,
  url: payload.url,
  fileType: payload.fileType,
  host: payload.host,
});

export interface ProfilePhotos {
  profilePhoto: UploadedImage | null;
  additionalPhotos: UploadedImage[];
}

/**
 * The *structured* rating systems — the numeric scales. Values mirror the
 * rating-scale `short_name`s so they map straight onto user_<sport>_rating_scales
 * rows. Self-rate is deliberately NOT here: it's a categorical skill level, not a
 * scale (see SELF_RATE_SKILL_LEVELS) — see screens/Find/DB-DECISION-ONBOARDING.md.
 */
export enum RatingSystem {
  Vair = 'VAIR',
  Dupr = 'DUPR',
  Ntrp = 'NTRP',
  Utr = 'UTR',
  Wpr = 'WPR',
  Nprp = 'NPRP',
}

/**
 * "Self rate" options — the displayed `skill_levels` rows in rank order. Persists
 * to `users.<sport>_skill_level_id` (categorical), matching the game side's
 * `event_groups.self_rated_skill_level_id`. Excludes the non-displayed
 * DO_NOT_PLAY / NO_RESPONSE rows.
 */
export const SELF_RATE_SKILL_LEVELS: { value: SkillLevelValuesEnum; label: string }[] = [
  { value: SkillLevelValuesEnum.BrandNew, label: 'Brand new' },
  { value: SkillLevelValuesEnum.Beginner, label: 'Beginner' },
  { value: SkillLevelValuesEnum.Intermediate, label: 'Intermediate' },
  { value: SkillLevelValuesEnum.Advanced, label: 'Advanced' },
  { value: SkillLevelValuesEnum.Pro, label: 'Pro' },
];

interface PlayingLevelSport {
  value: SportsEnum;
  label: string;
  iconSrc: string;
  /** A player can hold a rating in more than one system, so these are toggles. */
  ratingSystems: { value: RatingSystem; label: string }[];
}

/** Same sport glyphs the "List a game" chips use (screens/ListGame SPORT_OPTIONS). */
export const PLAYING_LEVEL_SPORTS: PlayingLevelSport[] = [
  {
    value: SportsEnum.Pickleball,
    label: 'Pickleball',
    iconSrc: '/images/programming/pickleball.png',
    ratingSystems: [
      { value: RatingSystem.Vair, label: 'VAIR' },
      { value: RatingSystem.Dupr, label: 'DUPR' },
    ],
  },
  {
    value: SportsEnum.Tennis,
    label: 'Tennis',
    iconSrc: '/images/programming/tennis.png',
    ratingSystems: [
      { value: RatingSystem.Ntrp, label: 'NTRP' },
      { value: RatingSystem.Utr, label: 'UTR' },
    ],
  },
  {
    value: SportsEnum.Padel,
    label: 'Padel',
    iconSrc: '/images/programming/padel.png',
    ratingSystems: [
      { value: RatingSystem.Wpr, label: 'WPR' },
      { value: RatingSystem.Nprp, label: 'NPRP' },
    ],
  },
];

export interface PlayingLevelValues {
  /** Sports the player picked, in selection order. */
  sports: SportsEnum[];
  /** sport → structured rating system → entered rating. Only toggled-on systems appear. */
  ratings: Partial<Record<SportsEnum, Partial<Record<RatingSystem, string>>>>;
  /**
   * sport → self-rated skill level. Key present = "Self rate" toggled on; `null` =
   * on but no level picked yet. Persists to `users.<sport>_skill_level_id`.
   */
  selfRatedSkillLevels: Partial<Record<SportsEnum, SkillLevelValuesEnum | null>>;
}

export interface PreferredCourtsValues {
  /** Venues the player picked, newest first. */
  venues: VenueAddressWithCity[];
  days: AvailabilityDay[];
  /** day → the time buckets chosen for it. Only selected days appear. */
  timesByDay: Partial<Record<AvailabilityDay, PlayerFinderTimeOfDayEnum[]>>;
}
